import React from 'react';
import Card from '../../components/ui/Card.jsx';
import Input from '../../components/ui/Input.jsx';
import Button from '../../components/ui/Button.jsx';
import { apiFetch } from '../../api.js';
import { useApi } from '../../hooks/useApi.js';
import { RouteContext } from '../../router.jsx';

const semesterOptions = [
  { value: '', label: 'Select semester' },
  { value: 'Fall', label: 'Fall' },
  { value: 'Spring', label: 'Spring' },
  { value: 'Summer', label: 'Summer' }
];

export default function StaffStudentEdit({ params }) {
  const { navigate } = React.useContext(RouteContext);
  const studentId = params?.id;
  const { data, loading, error } = useApi(`/api/students/${studentId}`, [studentId]);
  const [form, setForm] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState({ text: '', tone: 'info' });

  React.useEffect(() => {
    if (!data) return;
    setForm({
      firstName: data.first_name || '',
      lastName: data.last_name || '',
      username: data.username || '',
      personalEmail: data.personal_email || '',
      startSemester: data.start_semester || '',
      startYear: data.start_year ? String(data.start_year) : '',
      ban: data.ban === 1
    });
  }, [data]);

  if (!studentId) {
    return (
      <div className="page">
        <div className="alert error">Missing student id.</div>
      </div>
    );
  }

  const updateField = (key) => (event) => {
    const value = key === 'ban' ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    setMessage({ text: '', tone: 'info' });
    try {
      const startYear = parseInt(form.startYear, 10);
      await apiFetch(`/api/students/${studentId}`, {
        method: 'PUT',
        body: JSON.stringify({
          first_name: form.firstName.trim(),
          last_name: form.lastName.trim(),
          username: form.username.trim(),
          personal_email: form.personalEmail.trim(),
          start_semester: form.startSemester.trim(),
          start_year: Number.isFinite(startYear) ? startYear : null,
          ban: form.ban ? 1 : 0
        })
      });
      setMessage({ text: 'Student updated.', tone: 'info' });
    } catch (err) {
      setMessage({ text: err?.message || 'Failed to update student.', tone: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Edit Student</h1>
          <p className="subtle">Update contact details and access status.</p>
        </div>
        <Button variant="secondary" type="button" onClick={() => navigate(`/staff/students/${studentId}`)}>Back to Profile</Button>
      </div>

      {error ? <div className="alert error">{error}</div> : null}
      {message.text ? <div className={message.tone === 'error' ? 'alert error' : 'alert'}>{message.text}</div> : null}

      {loading || !form ? (
        <p className="subtle">Loading...</p>
      ) : (
        <Card title="Student Details">
          <div className="form-grid">
            <Input label="First Name" value={form.firstName} onChange={updateField('firstName')} />
            <Input label="Last Name" value={form.lastName} onChange={updateField('lastName')} />
            <Input label="Campus Username" value={form.username} onChange={updateField('username')} />
            <Input label="Personal Email" value={form.personalEmail} onChange={updateField('personalEmail')} />
            <label className="input-field">
              <span className="input-label">Start Semester</span>
              <select className="input" value={form.startSemester} onChange={updateField('startSemester')}>
                {semesterOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <Input label="Start Year" value={form.startYear} onChange={updateField('startYear')} />
            <div className="input-field">
              <span className="input-label">Hold Access</span>
              <label className="switch">
                <input type="checkbox" checked={form.ban} onChange={updateField('ban')} />
                <span className="slider" />
              </label>
              <span className="input-helper">Disable student logins and requests.</span>
            </div>
          </div>
          <div className="form-actions">
            <Button
              variant="secondary"
              type="button"
              onClick={() => navigate(`/staff/students/${studentId}/delete`)}
            >
              Delete
            </Button>
            <Button type="button" disabled={saving} onClick={handleSave}>{saving ? 'Saving...' : 'Save Changes'}</Button>
          </div>
        </Card>
      )}
    </div>
  );
}
